import * as THREE from 'three'
import { markRaw } from 'vue'

/**
 * Full-screen procedural fog drifting across the chamber plate.
 *
 * This deliberately lives outside the dice scene: it is an overlay on the
 * background image, so the atmosphere moves through the whole room rather than
 * being trapped inside the small canvas the die is rendered in.
 *
 * The fog is fbm-layered simplex noise with domain warping, masked so it pools
 * in the shadowed edges and thins out over the lit ring.
 */

const VERTEX_SHADER = `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy, 0.0, 1.0);
  }
`

// Ashima / Gustavson simplex noise, the standard GLSL implementation.
const SIMPLEX = `
  vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
  vec2 mod289(vec2 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
  vec3 permute(vec3 x) { return mod289(((x * 34.0) + 1.0) * x); }

  float snoise(vec2 v) {
    const vec4 C = vec4(0.211324865405187, 0.366025403784439,
                       -0.577350269189626, 0.024390243902439);
    vec2 i  = floor(v + dot(v, C.yy));
    vec2 x0 = v -   i + dot(i, C.xx);
    vec2 i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
    vec4 x12 = x0.xyxy + C.xxzz;
    x12.xy -= i1;
    i = mod289(i);
    vec3 p = permute(permute(i.y + vec3(0.0, i1.y, 1.0))
                            + i.x + vec3(0.0, i1.x, 1.0));
    vec3 m = max(0.5 - vec3(dot(x0, x0), dot(x12.xy, x12.xy),
                            dot(x12.zw, x12.zw)), 0.0);
    m = m * m; m = m * m;
    vec3 x = 2.0 * fract(p * C.www) - 1.0;
    vec3 h = abs(x) - 0.5;
    vec3 ox = floor(x + 0.5);
    vec3 a0 = x - ox;
    m *= 1.79284291400159 - 0.85373472095314 * (a0 * a0 + h * h);
    vec3 g;
    g.x  = a0.x  * x0.x  + h.x  * x0.y;
    g.yz = a0.yz * x12.xz + h.yz * x12.yw;
    return 130.0 * dot(m, g);
  }
`

const FRAGMENT_SHADER = `
  precision highp float;

  uniform float uTime;
  uniform vec2  uResolution;
  uniform vec2  uRing;
  uniform float uIntensity;
  uniform vec3  uColor;

  varying vec2 vUv;

  ${SIMPLEX}

  float fbm(vec2 p) {
    float total = 0.0;
    float amplitude = 0.5;
    for (int i = 0; i < 5; i++) {
      total += snoise(p) * amplitude;
      p *= 2.02;
      amplitude *= 0.5;
    }
    return total;
  }

  void main() {
    // Correct for viewport shape so the fog cells stay round.
    vec2 uv = vUv;
    vec2 p = uv * vec2(uResolution.x / uResolution.y, 1.0);

    float t = uTime * 0.035;

    // Domain warping: the fog curls instead of sliding as a flat sheet.
    vec2 warp = vec2(
      fbm(p * 1.6 + vec2(t * 1.3, t * 0.7)),
      fbm(p * 1.6 + vec2(-t * 0.9, t * 1.1) + 17.3)
    );

    float density = fbm(p * 2.1 + warp * 0.9 + vec2(t * 0.6, -t * 0.35));
    density = density * 0.5 + 0.5;

    // A second, slower bank drifting the other way adds depth.
    float bank = fbm(p * 1.1 - vec2(t * 0.45, t * 0.2) + 9.1) * 0.5 + 0.5;
    density = mix(density, bank, 0.4);

    // Pool in the shadowed edges, thin out over the lit ring.
    float ring = distance(uv, uRing) ;
    float clearing = smoothstep(0.05, 0.42, ring);
    float edges = smoothstep(0.15, 0.75, length(uv - vec2(0.5)));
    float floorBias = smoothstep(0.15, 0.95, uv.y);

    // Every term keeps a floor so the room never has bald, fogless patches.
    float mask = clamp(
      (0.25 + 0.75 * clearing) *
      (0.55 + 0.65 * edges) *
      (0.45 + 0.7 * floorBias),
      0.0, 1.0
    );

    float fog = smoothstep(0.30, 0.86, density) * mask * uIntensity;

    gl_FragColor = vec4(uColor * fog, fog);
  }
`

export class ChamberFog {
  constructor() {
    this.renderer = null
    this.scene = null
    this.camera = null
    this.material = null
    this.elapsed = 0
    this.running = false
    this.frameId = null
    this.lastTime = 0
  }

  init(canvas, { ringX = 0.5, ringY = 0.6, intensity = 1, color = 0x9fd8f0 } = {}) {
    this.renderer = markRaw(new THREE.WebGLRenderer({
      canvas,
      alpha: true,
      antialias: false,
      powerPreference: 'low-power'
    }))
    // The fog is all low frequency, so half resolution is free quality.
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1) * 0.6)

    this.scene = markRaw(new THREE.Scene())
    this.camera = markRaw(new THREE.Camera())

    this.material = markRaw(new THREE.ShaderMaterial({
      vertexShader: VERTEX_SHADER,
      fragmentShader: FRAGMENT_SHADER,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      uniforms: {
        uTime: { value: 0 },
        uResolution: { value: new THREE.Vector2(1, 1) },
        uRing: { value: new THREE.Vector2(ringX, 1 - ringY) },
        uIntensity: { value: intensity },
        uColor: { value: new THREE.Color(color) }
      }
    }))

    const quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.material)
    quad.frustumCulled = false
    this.scene.add(quad)
    this.quad = markRaw(quad)

    this.resize()
    return true
  }

  setRing(ringX, ringY) {
    this.material?.uniforms.uRing.value.set(ringX, 1 - ringY)
  }

  resize() {
    if (!this.renderer) return
    const canvas = this.renderer.domElement
    const width = canvas.clientWidth || window.innerWidth
    const height = canvas.clientHeight || window.innerHeight

    this.renderer.setSize(width, height, false)
    this.material.uniforms.uResolution.value.set(width, height)
  }

  start() {
    if (this.running || !this.renderer) return
    this.running = true
    this.lastTime = 0

    const loop = (time) => {
      if (!this.running) return
      const delta = this.lastTime ? (time - this.lastTime) / 1000 : 0
      this.lastTime = time
      this.elapsed += Math.min(delta, 0.1)

      this.material.uniforms.uTime.value = this.elapsed
      this.renderer.render(this.scene, this.camera)
      this.frameId = requestAnimationFrame(loop)
    }

    this.frameId = requestAnimationFrame(loop)
  }

  stop() {
    this.running = false
    if (this.frameId) cancelAnimationFrame(this.frameId)
    this.frameId = null
  }

  dispose() {
    this.stop()
    this.quad?.geometry?.dispose()
    this.material?.dispose()
    this.renderer?.dispose()
    this.renderer = null
    this.scene = null
    this.camera = null
    this.material = null
    this.quad = null
  }
}

export default ChamberFog
